import type { AssetTypes } from 'js/src/client';
import type { UploadModalContext } from 'js/src/data/modals/modals-types';
import { MODAL_IDS } from 'js/src/data/modals/modals-types';
import type { BackgroundAndIconAssetContainerTypes } from 'js/src/data/uploads';
import { ImmutableIsList, type ImmutableMap } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

export function isVideoNameEditingModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.VIDEO_NAME_EDITING;
}

export function isProjectDeleteModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.PROJECT_DELETE;
}

export function isProjectCreateModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.PROJECT_CREATE;
}

export function isVideoListMembersModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.VIDEO_LIST_MEMBERS_UPDATE;
}

export function isTeamMemberInvitationModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.TEAM_MEMBER_INVITATION_MODAL;
}

export function isVideoPreviewModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.VIDEO_PREVIEW;
}

export function isFeatureLockModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.FEATURE_LOCK;
}

export function isSubmitFeedbackModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.SUBMIT_FEEDBACK;
}

export function isExportClipsModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.EXPORT_CLIPS;
}

export function isPremiumMediaUpgradeModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.PREMIUM_MEDIA_UPGRADE;
}

export function getUploadSessionId(state: RootState): string | null {
    if (isUploadModalShowing(state)) {
        const context = getModalContext<UploadModalContext>(state);
        if (!context) return null;
        return context.get('uploadSessionId');
    }
    return null;
}

export function getUploadConfirmedCallback(
    state: RootState,
): ((uploadPromise: Promise<string[]>) => void) | null {
    if (isUploadModalShowing(state)) {
        const context = getModalContext<UploadModalContext>(state);
        if (!context) return null;
        return context.toJS().uploadConfirmedCallback || null;
    }
    return null;
}

export function getUploadAssetContainerTypes(state: RootState) {
    if (isUploadModalShowing(state)) {
        const context = getModalContext<UploadModalContext>(state);
        if (!context) return null;
        const assetContainerTypes = context.get('assetContainerTypes');
        if (ImmutableIsList(assetContainerTypes)) {
            return assetContainerTypes.toJS() as BackgroundAndIconAssetContainerTypes;
        }
        return assetContainerTypes;
    }
    return null;
}

export function getConstrainToAssetTypes(state: RootState): AssetTypes[] | null {
    if (isUploadModalShowing(state)) {
        const context = getModalContext<UploadModalContext>(state);
        if (!context) return null;
        const constrainToAssetTypes = context.get('constrainToAssetTypes');
        if (!constrainToAssetTypes) return null;
        // Convert Immutable.List to regular array if needed
        if (Array.isArray(constrainToAssetTypes)) {
            return constrainToAssetTypes;
        }
        return constrainToAssetTypes.toJS();
    }
    return null;
}

export function getConstrainToSingleFile(state: RootState): boolean | null {
    if (isUploadModalShowing(state)) {
        const context = getModalContext<UploadModalContext>(state);
        if (!context) return null;
        return context.get('constrainToSingleFile') || null;
    }
    return null;
}

export function isUploadModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.UPLOAD_MEDIA;
}

export function isLoginRequiredShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.LOGIN_REQUIRED_FOR_COMMENTING;
}

export function isCropModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.CROP;
}

export function isTrimModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.TRIM;
}

export function isInsertTrimModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.INSERT_TRIM;
}

export function isApprovalStatusModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.APPROVAL_STATUS;
}

export function isCreateBlueprintFromVideoModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.CREATE_BLUEPRINT_FROM_VIDEO;
}

export function isTranscriptGeneratedModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.TRANSCRIPT_GENERATED;
}

export function isRemoveTeamMemberModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.REMOVE_TEAM_MEMBER;
}

export function isUserSettingsModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.USER_SETTINGS;
}

export function isRecordYourselfModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.RECORD_YOURSELF;
}

export function isRecordYourScreenModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.RECORD_YOUR_SCREEN;
}

export function isGenericErrorModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.GENERIC_ERROR;
}

export function isVoicedrivenSceneCreationModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.VOICEDRIVEN_SCENE_CREATION;
}

export function isAIVoiceSelectionModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.AI_VOICE_SELECTION;
}

export function isAIAvatarVoicePresetSelectionModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.AI_AVATAR_VOICE_PRESET_SELECTION;
}

export function isEditPronunciationModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.EDIT_PRONUNCIATION;
}

export function isMediaDetailsModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.MEDIA_DETAILS;
}

export function isGenerativeMediaDetailsModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.GENERATIVE_MEDIA_DETAILS;
}

export function isDeletePronunciationModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.DELETE_PRONUNCIATION;
}

export function isAudioVisualizationModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.AUDIO_VISUALIZATION;
}

export function isClipPreviewModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.CLIP_PREVIEW;
}

export function isClipCreatorTemplatesModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.CLIP_CREATOR_TEMPLATES;
}

export function isSketchTemplatesModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.SKETCH_TEMPLATES;
}

export function isCreateBlueprintModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.CREATE_BLUEPRINT;
}

export function isRenameFolderModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.RENAME_FOLDER;
}

export function isDeleteFolderModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.DELETE_FOLDER;
}

export function isCreateFolderModalShowing(state: RootState): boolean {
    return state.modals.visible_stack.peek() === MODAL_IDS.CREATE_FOLDER;
}

export function getModalContext<T extends object>(state: RootState) {
    const context = state.modals.visible_stack_context.peek();
    if (context) {
        return context as ImmutableMap<T>;
    }
    return null;
}
